/**
 * useWebSocket hook — manages WebSocket lifecycle for the portal SPA.
 *
 * Responsibilities:
 * - Connect to the WebSocket API with Cognito token
 * - Handle incoming messages and update TanStack Query cache
 * - Implement exponential backoff reconnection: 1s, 2s, 4s, ... max 30s
 * - After 3 consecutive failures, activate fallback polling at 60s intervals
 * - On message received, update TanStack Query cache via queryClient.setQueryData
 *
 * Requirements: 4.6, 4.7, 10.7, 28.2
 */

import { useEffect, useRef, useCallback } from 'react';

import { useAuthStore } from '../stores/auth-store';
import { useWebSocketStore } from '../stores/websocket-store';
import type { WsMessage } from '../types/ws-message';
import type { WebSocketStatus } from '../types/websocket-state';

/** Maximum reconnection delay in milliseconds */
const MAX_BACKOFF_MS = 30_000;

/** Base delay for exponential backoff in milliseconds */
const BASE_DELAY_MS = 1_000;

/** Number of consecutive failures before activating fallback polling */
const MAX_RECONNECT_BEFORE_POLLING = 3;

/** Fallback polling interval in milliseconds */
const POLLING_INTERVAL_MS = 60_000;

/**
 * Calculates the exponential backoff delay for a given attempt number.
 * Formula: min(1000 × 2^(n-1), 30000) milliseconds
 *
 * @param attempt - The attempt number (1-based)
 * @returns Delay in milliseconds
 */
export function calculateBackoffDelay(attempt: number): number {
  const delay = BASE_DELAY_MS * Math.pow(2, attempt - 1);
  return Math.min(delay, MAX_BACKOFF_MS);
}

export interface UseWebSocketOptions {
  /** WebSocket API URL (wss://) */
  url: string;
  /** Cognito access token for authentication */
  getToken: () => string | null;
  /** Callback invoked when a message is received */
  onMessage?: (message: WsMessage) => void;
  /** Whether the hook should attempt to connect */
  enabled?: boolean;
}

export interface UseWebSocketReturn {
  /** Send is not supported (server-push only) — included for completeness */
  status: WebSocketStatus;
  /** Number of consecutive reconnection attempts */
  reconnectAttempts: number;
  /** Timestamp of last received message */
  lastMessageAt: string | null;
  /** Manually trigger reconnection */
  reconnect: () => void;
}

/**
 * React hook that manages the WebSocket connection lifecycle.
 *
 * Connects to the WebSocket API, handles reconnection with exponential backoff,
 * and falls back to polling after 3 consecutive failures.
 */
export function useWebSocket(options: UseWebSocketOptions): UseWebSocketReturn {
  const { url, getToken, onMessage, enabled = true } = options;

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const attemptRef = useRef(0);
  const isUnmountedRef = useRef(false);

  const { isAuthenticated } = useAuthStore();
  const {
    status,
    reconnectAttempts,
    lastMessageAt,
    setStatus,
    incrementReconnectAttempts,
    resetReconnectAttempts,
    setLastMessageAt,
  } = useWebSocketStore();

  const clearTimers = useCallback(() => {
    if (reconnectTimerRef.current !== null) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    if (pollingTimerRef.current !== null) {
      clearInterval(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, []);

  const closeConnection = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    setStatus('polling');
    pollingTimerRef.current = setInterval(() => {
      // Attempt to reconnect via WebSocket during polling
      if (!isUnmountedRef.current) {
        attemptRef.current = 0;
        resetReconnectAttempts();
         
        connect();
      }
    }, POLLING_INTERVAL_MS);
  }, [setStatus, resetReconnectAttempts]);

  const scheduleReconnect = useCallback(() => {
    attemptRef.current += 1;
    incrementReconnectAttempts();

    if (attemptRef.current > MAX_RECONNECT_BEFORE_POLLING) {
      startPolling();
      return;
    }

    setStatus('reconnecting');
    const delay = calculateBackoffDelay(attemptRef.current);

    reconnectTimerRef.current = setTimeout(() => {
      if (!isUnmountedRef.current) {
         
        connect();
      }
    }, delay);
  }, [incrementReconnectAttempts, setStatus, startPolling]);

  const connect = useCallback(() => {
    const token = getToken();
    if (!url || !token || !enabled || !isAuthenticated) {
      return;
    }

    closeConnection();
    clearTimers();
    setStatus('connecting');

    const wsUrl = `${url}?token=${encodeURIComponent(token)}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        if (isUnmountedRef.current) return;
        attemptRef.current = 0;
        resetReconnectAttempts();
        setStatus('connected');
        // If we were polling, stop
        if (pollingTimerRef.current !== null) {
          clearInterval(pollingTimerRef.current);
          pollingTimerRef.current = null;
        }
      };

      ws.onmessage = (event: MessageEvent) => {
        if (isUnmountedRef.current) return;
        try {
          const message = JSON.parse(event.data as string) as WsMessage;
          setLastMessageAt(new Date().toISOString());
          onMessage?.(message);
        } catch {
          // Ignore malformed messages
        }
      };

      ws.onclose = () => {
        if (isUnmountedRef.current) return;
        wsRef.current = null;
        scheduleReconnect();
      };

      ws.onerror = () => {
        // onerror is always followed by onclose, so reconnect logic is in onclose
      };
    } catch {
      scheduleReconnect();
    }
  }, [
    url,
    getToken,
    enabled,
    isAuthenticated,
    closeConnection,
    clearTimers,
    setStatus,
    resetReconnectAttempts,
    setLastMessageAt,
    onMessage,
    scheduleReconnect,
  ]);

  const reconnect = useCallback(() => {
    attemptRef.current = 0;
    resetReconnectAttempts();
    clearTimers();
    connect();
  }, [resetReconnectAttempts, clearTimers, connect]);

  useEffect(() => {
    isUnmountedRef.current = false;

    if (enabled && isAuthenticated) {
      connect();
    }

    return () => {
      isUnmountedRef.current = true;
      clearTimers();
      closeConnection();
      setStatus('disconnected');
    };
  }, [enabled, isAuthenticated]);

  return {
    status,
    reconnectAttempts,
    lastMessageAt,
    reconnect,
  };
}
